import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';

import useAppContext from 'flow/hooks/useAppContext';
import useMeetingDeviceChangeListener from 'flow/hooks/useMeetingDeviceChangeListener';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import {
    selectActiveMeetingChatName,
    selectActiveMeetingHasAccessToChat,
    selectActiveMeetingIsNarrowWindow,
    selectActiveMeetingViewIsFullscreen,
    selectSenderName,
} from 'flow/selectors/meeting-selectors';
import { sliceUUIDFromUserName } from 'flow/utils/meeting-utils';
import { twMerge } from 'tailwind-merge';
import MeetingViewMenu from './shared/MeetingViewMenu';
import MeetingWaitingRoomPill from './MeetingWaitingRoomPill';
import { HEADER_BUTTON_CLASS } from './MeetingHeaderButton';

const MeetingHeader = () => {
    const { i18next } = useAppContext();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const hasAccessToChat = useAppSelector(selectActiveMeetingHasAccessToChat);
    const isFullscreenView = useAppSelector(
        selectActiveMeetingViewIsFullscreen,
    );
    const isNarrowWindow = useAppSelector(selectActiveMeetingIsNarrowWindow);
    const chatName = useAppSelector(selectActiveMeetingChatName);
    const senderName = useAppSelector(selectSenderName);
    const ownDisplayName = sliceUUIDFromUserName(senderName);
    const meetingTitle = ownDisplayName
        ? `${i18next.t('videoconference.meeting')} (${ownDisplayName})`
        : i18next.t('videoconference.meeting');

    useMeetingDeviceChangeListener();

    return (
        <div
            className={twMerge(
                // Always a full-width bar at the top; the content (video + side
                // panels) is framed below it, so the header never shrinks or
                // hides for a panel.
                'h-meeting-header top-0 z-50 flex w-full items-center justify-between bg-black text-white',
                isFullscreenView || isNarrowWindow ? 'fixed start-0' : '',
            )}
        >
            <div className="flex-1 overflow-hidden ps-3 pe-2 text-start text-ellipsis whitespace-nowrap">
                {hasAccessToChat && chatName ? chatName : meetingTitle}
            </div>

            <div className="end-0 top-5 me-6 flex items-center justify-end gap-3">
                <MeetingWaitingRoomPill />
                {!!ownExtensionKey && (
                    <MeetingViewMenu
                        className={twMerge(
                            HEADER_BUTTON_CLASS,
                            'hover:darken-10',
                        )}
                    />
                )}
            </div>
        </div>
    );
};

export default MeetingHeader;
